import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Sélecteur de source d'approvisionnement du formulaire de demande.
 * Animateur / chef de zone : uniquement les distributeurs de leur(s) zone(s).
 * Marketing / directrice (sans zone) : tous les distributeurs actifs.
 * Émet : { category: 'distributor', source: { id } }
 */
const ZoneDistributorSelector = ({ onChange, value, disabled = false }) => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [distributeurs, setDistributeurs] = useState([]);
  const [zoneNames, setZoneNames] = useState([]);
  const [selectedId, setSelectedId] = useState(value?.source?.id || '');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const myId = String(currentUser?._id || currentUser?.id || '');
        const res = await api.zones.getAll();
        const zones = res.data?.data || [];
        const zoneBound = ['animateur', 'chef_de_zone'].includes(currentUser?.role);
        const myZones = zoneBound
          ? zones.filter(
              (z) =>
                (z.animateurs || []).some((a) => String(a._id || a) === myId) ||
                String(z.chefDeZoneId?._id || z.chefDeZoneId || '') === myId
            )
          : zones;
        setZoneNames(zoneBound ? myZones.map((z) => z.name) : []);

        // Dédupliquer les distributeurs actifs des zones de l'utilisateur
        const byId = new Map();
        myZones.forEach((z) =>
          (z.distributeurs || []).forEach((d) => {
            if (d && d.isActive !== false) byId.set(String(d._id), d);
          })
        );
        setDistributeurs([...byId.values()]);
      } catch (err) {
        toast.error('Erreur lors du chargement des distributeurs de votre zone');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentUser]);

  const handleChange = (e) => {
    const id = e.target.value;
    setSelectedId(id);
    if (onChange) {
      onChange(id ? { category: 'distributor', source: { id } } : { category: 'distributor', source: null });
    }
  };

  return (
    <div>
      <p className="text-xs text-gray-500 mb-2">
        {zoneNames.length > 0 ? <>Distributeurs de votre zone&nbsp;: {zoneNames.join(', ')}</> : 'Tous les distributeurs actifs'}
      </p>
      {loading ? (
        <div className="animate-pulse h-10 bg-gray-200 rounded" />
      ) : distributeurs.length === 0 ? (
        <p className="text-sm text-red-600">
          {zoneNames.length > 0 || ['animateur', 'chef_de_zone'].includes(currentUser?.role)
            ? 'Aucun distributeur rattaché à votre zone. Contactez votre chef de zone.'
            : 'Aucun distributeur actif.'}
        </p>
      ) : (
        <select
          className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          value={selectedId}
          onChange={handleChange}
          disabled={disabled}
        >
          <option value="">Sélectionner un distributeur</option>
          {distributeurs.map((d) => (
            <option key={d._id} value={d._id}>
              {d.name}
              {d.code ? ` (${d.code})` : ''}
              {d.city ? ` — ${d.city}` : ''}
            </option>
          ))}
        </select>
      )}
    </div>
  );
};

export default ZoneDistributorSelector;
